import { useState } from 'react';
import type { CatalogItem } from '@matix/types';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { callCenterApi, ccErrorMessage } from './api';
import { useCallCenter } from './store';
import { useMoneyFormatter } from './hooks';
import { useEngineQuote } from './useEngineQuote';
import { CustomerPanel } from './CustomerPanel';
import { CatalogPanel } from './CatalogPanel';
import { ModifierPicker } from './ModifierPicker';
import { DeliveryTimePicker } from '../delivery/DeliveryTimePicker';
import { formatDraftDateTime, fromNow, toDraftValue } from '../delivery/deliveryTime';

/**
 * The agent's order-taking screen.
 *
 * The layout follows the CALL, not the data model. A phone order happens in a
 * fixed order — the phone rings, you find out who it is, which branch serves
 * them, what they want, you read back the total, you send — and the screen is
 * that sequence rather than a form with everything visible at once. Somebody
 * looking at this for eight hours should never have to decide what to do next.
 *
 * The basket is a permanent right-hand column, because it is the thing the
 * agent reads out loud and the thing they are asked to change. It shows what
 * the customer PAYS in the largest type on the screen — promotions, membership
 * benefits and coupons are all priced by the server and land there, so the
 * figure quoted on the phone is the one charged at the door.
 */
export function NewOrderScreen(): JSX.Element {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();

  const bootstrap = useCallCenter((s) => s.bootstrap);
  const branchId = useCallCenter((s) => s.branchId);
  const setBranch = useCallCenter((s) => s.setBranch);
  const customer = useCallCenter((s) => s.customer);
  const address = useCallCenter((s) => s.address);
  const lines = useCallCenter((s) => s.lines);
  const addItem = useCallCenter((s) => s.addItem);
  const setQty = useCallCenter((s) => s.setQty);
  const removeLine = useCallCenter((s) => s.removeLine);
  const discount = useCallCenter((s) => s.discount);
  const setDiscount = useCallCenter((s) => s.setDiscount);
  const deliveryFee = useCallCenter((s) => s.deliveryFee);
  const setDeliveryFee = useCallCenter((s) => s.setDeliveryFee);
  const deliveryAt = useCallCenter((s) => s.deliveryAt);
  const setDeliveryAt = useCallCenter((s) => s.setDeliveryAt);
  const note = useCallCenter((s) => s.note);
  const setNote = useCallCenter((s) => s.setNote);
  const couponCode = useCallCenter((s) => s.couponCode);
  const setCouponCode = useCallCenter((s) => s.setCouponCode);
  const couponError = useCallCenter((s) => s.couponError);
  const giftCard = useCallCenter((s) => s.giftCard);
  const setGiftCard = useCallCenter((s) => s.setGiftCard);
  const prepaidId = useCallCenter((s) => s.prepaidPaymentTypeId);
  const setPrepaid = useCallCenter((s) => s.setPrepaid);
  const idempotencyKey = useCallCenter((s) => s.idempotencyKey);
  const newOrder = useCallCenter((s) => s.newOrder);
  const quotePending = useCallCenter((s) => s.quotePending);
  // Subscribing to `lines` above is what makes this recompute; `totals` is a
  // stable function reference and would never re-render on its own.
  const totals = useCallCenter((s) => s.totals)();

  // Keeps promotions, membership benefits and the coupon in step with the
  // basket. Not decoration: the server applies them regardless and then refuses
  // a payment that does not match, so this is what makes Send Order work at all.
  useEngineQuote();

  const [sending, setSending] = useState(false);
  const [lastSentNo, setLastSentNo] = useState<number | null>(null);
  const [modifierItem, setModifierItem] = useState<CatalogItem | null>(null);
  const [showExtras, setShowExtras] = useState(false);

  const settings = bootstrap?.settings ?? null;
  const branches = bootstrap?.branches ?? [];
  const isDelivery = (settings?.defaultOrderType ?? 2) === 2;
  const addressMissing = isDelivery && (settings?.requireAddress ?? true) && !address;

  const canSend =
    !sending && branchId != null && customer != null && lines.length > 0 && !addressMissing;

  function handlePick(item: CatalogItem): void {
    // Only interrupt for an item that actually has choices to make. A dialog
    // that opens on every tap to say "nothing to choose" is one an agent learns
    // to dismiss before reading.
    if ((item.modifier_groups?.length ?? 0) > 0) {
      setModifierItem(item);
      return;
    }
    addItem(item);
  }

  async function handleSend(): Promise<void> {
    if (!canSend || branchId == null || !customer) return;
    setSending(true);
    try {
      const created = await callCenterApi.agent.create({
        idempotency_key: idempotencyKey,
        branch_id: branchId,
        customer_id: customer.id,
        address_id: address?.id ?? null,
        order_type: settings?.defaultOrderType ?? 2,
        items: useCallCenter.getState().toPayload(),
        discount_value: totals.discount,
        delivery_fee: totals.deliveryFee,
        // Null = "now" — the server stamps the send time, as it always did.
        delivery_at: deliveryAt,
        note: note.trim() || null,
        prepaid: prepaidId != null ? { payment_type_id: prepaidId } : null,
        coupon_code: couponCode.trim() || null,
        gift_card: giftCard.number.trim()
          ? { card_number: giftCard.number.trim(), pin: giftCard.pin.trim() || null }
          : null,
        // The promotion/membership-EXCLUDED figure — see the store's totals().
        expected_total: totals.total,
      });
      setLastSentNo(created.entryNo ?? created.receiptId);
      toast(
        t({
          en: `Order #${created.entryNo ?? created.receiptId} sent to ${created.branchName ?? 'the branch'}`,
          ar: `تم إرسال الطلب #${created.entryNo ?? created.receiptId} إلى ${created.branchName ?? 'الفرع'}`,
        }),
        'success',
      );
      newOrder();
      setShowExtras(false);
    } catch (err) {
      toast(ccErrorMessage(err, 'Failed to send order'), 'error');
    } finally {
      setSending(false);
    }
  }

  if (!bootstrap?.provisioned) {
    return (
      <div className="cc-empty">
        <div className="cc-empty-title">{t({ en: 'Call Center', ar: 'مركز الاتصال' })}</div>
        <p>
          {t({
            en: 'This company has not installed the Call Center module yet (migration 046).',
            ar: 'لم يتم تثبيت وحدة مركز الاتصال لهذه الشركة بعد (الترحيل 046).',
          })}
        </p>
      </div>
    );
  }

  if (!settings?.isEnabled) {
    return (
      <div className="cc-empty">
        <div className="cc-empty-title">{t({ en: 'Call Center is off', ar: 'مركز الاتصال متوقف' })}</div>
        <p>
          {t({
            en: 'An administrator can switch it on under Settings → Call Center in the admin app.',
            ar: 'يمكن للمسؤول تفعيله من الإعدادات ← مركز الاتصال في تطبيق الإدارة.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="cc-order">
      <div className="cc-order-left">
        <CustomerPanel />

        <section className={`cc-step cc-panel-card ${branchId != null ? 'cc-step--done' : ''}`}>
          <h3>
            <span className="cc-step-n">2</span>
            {t({ en: 'Which branch?', ar: 'أي فرع؟' })}
          </h3>
          <div className="cc-branches">
            {branches.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`cc-branch ${branchId === b.id ? 'cc-branch--on' : ''}`}
                onClick={() => setBranch(b.id)}
              >
                <span>{lang === 'ar' ? b.nameAr || b.name : b.name}</span>
                {b.openCount > 0 && <span className="cc-branch-count">{b.openCount}</span>}
              </button>
            ))}
          </div>
        </section>

        {branchId != null && customer != null ? (
          <CatalogPanel branchId={branchId} onPick={handlePick} />
        ) : (
          <div className="cc-empty cc-empty--inline">
            {branchId == null
              ? t({ en: 'Pick a branch to see its menu.', ar: 'اختر فرعاً لعرض قائمته.' })
              : t({ en: 'Find the caller first.', ar: 'ابحث عن المتصل أولاً.' })}
          </div>
        )}
      </div>

      <aside className="cc-order-right">
        <header className="cc-basket-head">
          <span>{t({ en: 'Order', ar: 'الطلب' })}</span>
          {lastSentNo != null && (
            <span className="cc-basket-sent">
              {t({ en: 'Sent', ar: 'أُرسل' })} #{lastSentNo}
            </span>
          )}
        </header>

        <div className="cc-basket-lines">
          {lines.length === 0 && (
            <div className="cc-basket-empty">
              {t({ en: 'No items yet', ar: 'لا توجد أصناف بعد' })}
            </div>
          )}
          {lines.map((line) => (
            <div key={line.key} className="cc-basket-line">
              <div className="cc-basket-line-main">
                <span className="cc-basket-line-name">
                  {lang === 'ar' ? line.nameAr || line.name : line.name}
                </span>
                <span className="cc-basket-line-amt">{money(line.qty * line.unitPrice)}</span>
              </div>
              {line.modifiers.length > 0 && (
                <div className="cc-basket-line-mods">
                  {line.modifiers.map((m) => m.name).join(' · ')}
                </div>
              )}
              {line.notes && <div className="cc-basket-line-note">{line.notes}</div>}
              <div className="cc-basket-line-ctl">
                <button type="button" onClick={() => setQty(line.key, line.qty - 1)}>−</button>
                <span>{line.qty}</span>
                <button type="button" onClick={() => setQty(line.key, line.qty + 1)}>+</button>
                <button
                  type="button"
                  className="cc-basket-line-x"
                  onClick={() => removeLine(line.key)}
                  title={t({ en: 'Remove', ar: 'حذف' })}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* The delivery fee is NOT an extra on a delivery-first call centre —
            it is on nearly every order, so it stays in the open. It sat behind
            the toggle below for one release and agents could not find it. */}
        {isDelivery && (
          <label className="cc-field cc-field--fee">
            <span>{t({ en: 'Delivery fee', ar: 'رسوم التوصيل' })}</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={deliveryFee}
              onChange={(e) => setDeliveryFee(Number(e.target.value) || 0)}
            />
          </label>
        )}

        {/* When the caller wants it. Most calls are "as soon as possible", so
            the default is Now and the picker only unfolds on request; the time
            lands on the receipt's CustomerDeliverDate, the slip, the kitchen
            ticket and every order card. */}
        {isDelivery && (
          <div className="cc-field cc-field--deliver-at">
            <span>
              {t({ en: 'Deliver at', ar: 'وقت التوصيل' })}
              {deliveryAt && (
                <strong className="delivery-time-summary">{formatDraftDateTime(deliveryAt)}</strong>
              )}
            </span>
            {deliveryAt ? (
              <>
                <DeliveryTimePicker value={deliveryAt} onChange={setDeliveryAt} />
                <button type="button" className="delivery-time-chip" onClick={() => setDeliveryAt(null)}>
                  {t({ en: 'As soon as possible', ar: 'في أقرب وقت' })}
                </button>
              </>
            ) : (
              <div className="delivery-time-quick">
                <span className="delivery-time-chip is-static">{t({ en: 'As soon as possible', ar: 'في أقرب وقت' })}</span>
                <button
                  type="button"
                  className="delivery-time-chip"
                  onClick={() => setDeliveryAt(toDraftValue(fromNow(30)))}
                >
                  {t({ en: 'Schedule…', ar: 'تحديد وقت…' })}
                </button>
              </div>
            )}
          </div>
        )}

        {/* What is left below is used on a minority of calls, so it collapses.
            An agent taking a plain order sees the basket, the fee and the Send
            button with nothing between them. */}
        <button
          type="button"
          className="cc-extras-toggle"
          onClick={() => setShowExtras((v) => !v)}
          aria-expanded={showExtras}
        >
          {showExtras
            ? t({ en: 'Hide options', ar: 'إخفاء الخيارات' })
            : t({
                en: 'More: discount · coupon · gift card · note',
                ar: 'المزيد: خصم · كوبون · بطاقة هدايا · ملاحظة',
              })}
        </button>

        {showExtras && (
          <div className="cc-basket-adjust">
            {(settings?.maxDiscountPercent ?? 0) > 0 && (
              <label className="cc-field">
                <span>
                  {t({ en: 'Discount', ar: 'الخصم' })} ({t({ en: 'max', ar: 'حد أقصى' })}{' '}
                  {settings?.maxDiscountPercent}%)
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                />
              </label>
            )}

            <label className="cc-field">
              <span>{t({ en: 'Coupon code', ar: 'كود الكوبون' })}</span>
              <input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder={t({ en: 'e.g. WELCOME10', ar: 'مثال WELCOME10' })}
              />
            </label>
            {couponError && <div className="cc-field-error">{couponError}</div>}

            <label className="cc-field">
              <span>{t({ en: 'Gift card number', ar: 'رقم بطاقة الهدايا' })}</span>
              <input
                value={giftCard.number}
                onChange={(e) => setGiftCard({ ...giftCard, number: e.target.value })}
              />
            </label>
            {giftCard.number.trim() && (
              <>
                <label className="cc-field">
                  <span>{t({ en: 'Gift card PIN', ar: 'رمز البطاقة' })}</span>
                  <input
                    value={giftCard.pin}
                    onChange={(e) => setGiftCard({ ...giftCard, pin: e.target.value })}
                  />
                </label>
                <div className="cc-hint">
                  {t({
                    en: 'A card used here must cover the whole order. For part of it, the branch takes the card at hand-over.',
                    ar: 'يجب أن تغطي البطاقة المستخدمة هنا الطلب بالكامل. لتغطية جزء منه، يأخذ الفرع البطاقة عند التسليم.',
                  })}
                </div>
              </>
            )}

            <label className="cc-field">
              <span>{t({ en: 'Note for the kitchen', ar: 'ملاحظة للمطبخ' })}</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} />
            </label>

            {settings?.allowPrepaid && !giftCard.number.trim() && (
              <label className="cc-field">
                <span>{t({ en: 'Already paid', ar: 'مدفوع مسبقاً' })}</span>
                <select
                  value={prepaidId ?? ''}
                  onChange={(e) => setPrepaid(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">
                    {t({ en: 'No — pay at handover', ar: 'لا — الدفع عند التسليم' })}
                  </option>
                  {bootstrap.paymentTypes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {lang === 'ar' ? p.nameAr || p.name : p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <div className="cc-basket-totals">
          <div className="cc-kv">
            <span>{t({ en: 'Subtotal', ar: 'المجموع' })}</span>
            <span>{money(totals.subtotal)}</span>
          </div>
          {totals.discount > 0 && (
            <div className="cc-kv">
              <span>{t({ en: 'Discount', ar: 'الخصم' })}</span>
              <span>&minus;{money(totals.discount)}</span>
            </div>
          )}
          {totals.couponDiscount > 0 && (
            <div className="cc-kv cc-kv--benefit">
              <span>
                {t({ en: 'Coupon', ar: 'كوبون' })} {couponCode.trim().toUpperCase()}
              </span>
              <span>&minus;{money(totals.couponDiscount)}</span>
            </div>
          )}
          {totals.deliveryFee > 0 && (
            <div className="cc-kv">
              <span>{t({ en: 'Delivery', ar: 'التوصيل' })}</span>
              <span>{money(totals.deliveryFee)}</span>
            </div>
          )}
          {totals.taxValue > 0 && (
            <div className="cc-kv">
              <span>
                {t({ en: 'VAT', ar: 'الضريبة' })} {bootstrap.taxPercentage}%
              </span>
              <span>{money(totals.taxValue)}</span>
            </div>
          )}
          {totals.engineDiscount > 0 &&
            (totals.engineBreakdown.length > 0 ? (
              totals.engineBreakdown.map((b, i) => (
                <div className="cc-kv cc-kv--benefit" key={`${b.name}-${i}`}>
                  <span>{b.name}</span>
                  <span>&minus;{money(b.amount)}</span>
                </div>
              ))
            ) : (
              <div className="cc-kv cc-kv--benefit">
                <span>{t({ en: 'Offers & benefits', ar: 'العروض والمزايا' })}</span>
                <span>&minus;{money(totals.engineDiscount)}</span>
              </div>
            ))}
          <div className="cc-kv cc-kv--total">
            <span>
              {t({ en: 'Customer pays', ar: 'المطلوب من العميل' })}
              {quotePending && (
                <em className="cc-quoting"> {t({ en: 'pricing…', ar: 'جارٍ التسعير…' })}</em>
              )}
            </span>
            <span>{money(totals.amountDue)}</span>
          </div>
        </div>

        {addressMissing && (
          <div className="cc-warn">
            {t({
              en: 'This delivery needs an address. Add one on the customer card.',
              ar: 'يحتاج هذا الطلب إلى عنوان. أضف عنواناً من بطاقة العميل.',
            })}
          </div>
        )}

        <button type="button" className="cc-send" disabled={!canSend} onClick={() => void handleSend()}>
          {sending
            ? t({ en: 'Sending…', ar: 'جارٍ الإرسال…' })
            : t({ en: 'Send order', ar: 'إرسال الطلب' })}
        </button>
      </aside>

      <ModifierPicker
        item={modifierItem}
        onCancel={() => setModifierItem(null)}
        onConfirm={({ qty, modifiers, notes }) => {
          if (modifierItem) addItem(modifierItem, { qty, modifiers, notes });
          setModifierItem(null);
        }}
      />
    </div>
  );
}
